import React, { useCallback, useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";

import { ChartOptions } from "chart.js";

interface PieChartProps {
  data: { label: string; value: number }[];
  colors: string[];
  tooltipFormatter?: (label: string, value: number) => string;
  size?: { min?: number; max?: number; factor?: number };
  className?: string;
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  colors,
  tooltipFormatter = (label, value) => `${label}: ${value}`,
  size = {},
  className = "pie-chart-container",
}) => {
  const { min = 64, max = 320, factor = 0.2 } = size;

  const getChartSize = useCallback(() => Math.min(Math.max(window.innerHeight * factor, min), max), [min, max, factor]);

  const [chartSize, setChartSize] = useState(getChartSize());

  useEffect(() => {
    const handleResize = () => setChartSize(getChartSize());
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getChartSize]);

  const chartData = {
    labels: data.map((item) => item.label),
    datasets: [
      {
        data: data.map((item) => item.value),
        backgroundColor: colors,
        hoverOffset: 8,
        borderWidth: data.length > 1 ? 2 : 0,
      },
    ],
  };

  const chartOptions: ChartOptions<"pie"> = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 4,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        backgroundColor: "#FFFFFF",
        titleColor: "#065C45",
        titleFont: { weight: "bold", size: 12 },
        bodyColor: "#100C06",
        bodyFont: { size: 12 },
        borderColor: "#D9D9D9",
        borderWidth: 1,
        cornerRadius: 8,
        padding: { top: 4, left: 8, right: 8, bottom: 8 },
        displayColors: false,
        callbacks: {
          title: (ctx) => ctx[0].label,
          label: (ctx) => tooltipFormatter(data[ctx.dataIndex].label, data[ctx.dataIndex].value),
        },
      },
    },
  };

  return (
    <div className={className} style={{ width: `${chartSize}px`, height: `${chartSize}px` }}>
      <Pie className="pie-chart" data={chartData} options={chartOptions} />
    </div>
  );
};
